import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCopy } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import FullscreenLoading from "../../Prescription/Components/FullScreenLoader";
import HeaderWithBack from "../../../components/HeaderWithBack";
import Confirm from "../../../components/modal/Confirm";
import { pendingDoctorLabel } from "../../../components/card/PendingPaymentCard";
import { formatCountdown, useCountdown } from "../../../hooks/useCountdown";
import {
  cancelPendingPayment,
  getPaymentStatus,
  PendingPayment,
} from "../../../services/pendingPaymentService";
import { useAuthStore } from "../../../stores/authStores";
import { useCheckoutStore } from "../../../stores/checkoutStore";
import { formatRupiah } from "../../../utils/formatPrice";
import { decryptFromBackend } from "../../../utils/urlDecryptor";
import InfoModal from '../../../components/modal/InfoModal';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetCheckout = useCheckoutStore((s) => s.resetCheckout);
  const storeToken = useAuthStore((s) => s.token);

  const teleconsultId = searchParams.get("teleconsult_id");
  const encToken = searchParams.get("enc_token");
  const resultCode = searchParams.get("resultCode");

  const token = useMemo(() => {
    if (storeToken) return storeToken;
    try {
      return encToken ? decryptFromBackend(encToken) : "";
    } catch {
      return "";
    }
  }, [storeToken, encToken]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [payment, setPayment] = useState<PendingPayment | null>(null);
  const [error, setError] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [copied, setCopied] = useState(false);
  const remaining = useCountdown(payment?.remaining_seconds);
  const leavingRef = useRef(false);
  const failureCountRef = useRef(0);

  const fetchStatus = useCallback(async () => {
    if (!teleconsultId || !token || leavingRef.current) return;
    try {
      const data = await getPaymentStatus(teleconsultId, token);
      if (leavingRef.current) return;
      failureCountRef.current = 0;
      setError(false);
      setPayment(data);
      sessionStorage.removeItem("pending_checkout_id");

      const routeToken = encToken || data.enc_token || "";
      if (routeToken) {
        localStorage.setItem(`teleconsult_token_${teleconsultId}`, routeToken);
      }

      if (data.payment_status === "paid") {
        localStorage.removeItem("teleconsult_disclaimer");
        resetCheckout();
        navigate(`/connect-doctor/${teleconsultId}/${routeToken}?stage=final-confirm`, { replace: true });
        return;
      }

      if (data.payment_status === "waiting" && !data.payment_url) {
        navigate(`/checkout-teleconsultation/${teleconsultId}`, { replace: true });
      }
    } catch (err: any) {
      if (leavingRef.current) return;
      if (err?.response?.status === 404) {
        navigate("/home", { replace: true });
        return;
      }
      failureCountRef.current += 1;
      if (failureCountRef.current >= 3) {
        setError(true);
      }
    }
  }, [teleconsultId, token, encToken, navigate, resetCheckout]);

  useEffect(() => {
    if (!teleconsultId || !token) {
      navigate("/home", { replace: true });
      return;
    }
    fetchStatus();
  }, [teleconsultId, token, fetchStatus, navigate]);

  useEffect(() => {
    const resync = (event?: Event) => {
      if (event?.type === "pageshow") leavingRef.current = false;
      if (document.visibilityState === "visible") fetchStatus();
    };
    window.addEventListener("pageshow", resync);
    window.addEventListener("focus", resync);
    document.addEventListener("visibilitychange", resync);
    return () => {
      window.removeEventListener("pageshow", resync);
      window.removeEventListener("focus", resync);
      document.removeEventListener("visibilitychange", resync);
    };
  }, [fetchStatus]);

  useEffect(() => {
    if (payment?.payment_status !== "waiting" && payment?.payment_status !== "verifying") return;
    const timer = setInterval(fetchStatus, 5000);
    return () => clearInterval(timer);
  }, [payment?.payment_status, fetchStatus]);

  useEffect(() => {
    if (payment?.payment_status === "waiting" && remaining === 0) {
      fetchStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining === 0]);

  const handleCancel = async () => {
    if (!teleconsultId) return;
    setCancelling(true);
    try {
      await cancelPendingPayment(teleconsultId, token);
      resetCheckout();
      navigate("/home", { replace: true });
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || "Gagal membatalkan pesanan");
      fetchStatus();
    } finally {
      setCancelling(false);
      setShowCancelConfirm(false);
    }
  };

  const handleCopyVa = () => {
    if (!payment?.va) return;
    navigator.clipboard?.writeText(payment.va);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center px-6 text-center">
        <p className="text-gray-700 mb-4">Status pembayaran tidak dapat dimuat.</p>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => {
              failureCountRef.current = 0;
              setError(false);
              fetchStatus();
            }}
            className="bg-teal-500 text-white px-6 py-2 rounded-lg"
          >
            Coba Lagi
          </button>
          <button onClick={() => navigate("/home", { replace: true })} className="text-gray-600 px-6 py-2">
            Kembali ke Beranda
          </button>
        </div>
      </div>
    );
  }

  if (!payment || payment.payment_status === "paid") {
    return <FullscreenLoading />;
  }

  if (payment.payment_status === "verifying") {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <HeaderWithBack title="Pembayaran" url="/home" />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <img src="/logo-linksehat.png" alt="Loading" className="w-16 h-16 animate-pulse mb-4" />
          <p className="text-lg font-semibold text-gray-800 mb-2">Memverifikasi Pembayaran</p>
          <p className="text-sm text-gray-600">
            Waktu pembayaran sudah habis. Kami sedang memastikan apakah pembayaran Anda sudah diterima. Mohon tunggu sebentar.
          </p>
        </div>
      </div>
    );
  }

  if (payment.payment_status !== "waiting") {
    const content = payment.payment_status === "expired"
      ? {
          title: "Waktu Pembayaran Habis",
          message: "Pesanan konsultasi ini tidak dapat dibayar lagi. Silakan buat pesanan baru untuk berkonsultasi.",
        }
      : {
          title: "Pesanan Dibatalkan",
          message: "Pesanan konsultasi ini sudah tidak aktif. Silakan buat pesanan baru untuk berkonsultasi.",
        };

    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <HeaderWithBack title="Pembayaran" url="/home" />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="text-lg font-semibold text-gray-800 mb-2">{content.title}</p>
          <p className="text-sm text-gray-600 mb-6">{content.message}</p>
          <button
            onClick={() => {
              resetCheckout();
              navigate("/home", { replace: true });
            }}
            className="bg-teal-500 text-white px-6 py-2.5 rounded-xl font-semibold"
          >
            Pesan Ulang
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col pb-8">
      <HeaderWithBack title="Selesaikan Pembayaran" url="/home" />

      <div className="px-4 mt-4">
        {resultCode && resultCode !== "00" && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-3 py-2 mb-3">
            Pembayaran belum diselesaikan di halaman Duitku. Lanjutkan pembayaran atau batalkan pesanan ini.
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-sm text-gray-500">Sisa waktu pembayaran</p>
          <p className="text-3xl font-bold text-amber-600 tabular-nums my-1">{formatCountdown(remaining)}</p>
          <p className="text-xs text-gray-400">
            Waktu ini sama dengan batas waktu di halaman Duitku. Pesanan otomatis dibatalkan jika belum dibayar sampai waktu habis.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow p-4 mt-4 text-sm space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-500">Dokter</span>
            <span className="text-gray-800 text-right">{pendingDoctorLabel(payment)}</span>
          </div>
          {payment.payment_method && (
            <div className="flex justify-between">
              <span className="text-gray-500">Metode</span>
              <span className="text-gray-800">{payment.payment_method}</span>
            </div>
          )}
          {payment.va && (
            <div className="flex justify-between items-center">
              <span className="text-gray-500">No. Virtual Account</span>
              <button onClick={handleCopyVa} className="flex items-center gap-1 font-semibold text-gray-800">
                {payment.va} <FiCopy className="text-teal-500" />
              </button>
            </div>
          )}
          {copied && <p className="text-xs text-teal-600 text-right">Nomor VA disalin</p>}
          <hr />
          <div className="flex justify-between font-semibold">
            <span>Total Bayar</span>
            <span className="text-teal-600">{payment.total != null ? formatRupiah(payment.total) : "-"}</span>
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          Halaman ini akan otomatis berpindah setelah pembayaran diterima.
        </p>

        <div className="flex flex-col gap-2 mt-4">
          <button
            onClick={() => {
              if (!payment.payment_url) return;
              leavingRef.current = true;
              window.location.href = payment.payment_url;
            }}
            disabled={!payment.payment_url}
            className="bg-teal-500 text-white py-2.5 rounded-xl font-semibold disabled:opacity-50"
          >
            Buka Halaman Pembayaran
          </button>
          {payment.can_create_payment ? (
            <button
              onClick={() => navigate(`/checkout-teleconsultation/${teleconsultId}`)}
              className="border border-teal-500 text-teal-600 py-2.5 rounded-xl font-medium"
            >
              Ganti Metode Pembayaran
            </button>
          ) : (
            <p className="text-xs text-gray-500 text-center">
              Sisa waktu kurang dari 1 menit, metode pembayaran tidak dapat diganti.
            </p>
          )}
          <button
            onClick={() => setShowCancelConfirm(true)}
            className="border border-red-300 text-red-500 py-2.5 rounded-xl font-medium"
          >
            Batalkan Pesanan
          </button>
          <button onClick={() => navigate("/home")} className="text-gray-500 text-sm py-1">
            Kembali ke Beranda
          </button>
        </div>
      </div>

      <Confirm
        show={showCancelConfirm}
        title="Batalkan Pesanan?"
        message="Pesanan konsultasi yang belum dibayar akan dibatalkan dan slot dokter dilepas. Anda dapat membuat pesanan baru setelahnya."
        confirmText="Batalkan"
        cancelText="Kembali"
        onConfirm={handleCancel}
        onCancel={() => setShowCancelConfirm(false)}
        loading={cancelling}
      />
      <InfoModal
        show={!!errorMessage}
        title="Terjadi Kesalahan"
        message={errorMessage || ''}
        onClose={() => setErrorMessage(null)}
      />
    </div>
  );
};

export default PaymentSuccess;
